import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import React, { useEffect } from 'react';
import { Controller } from 'react-hook-form';

import { TextField } from '../../TextField';
import { AuthTabsValues, useAuthContext } from '@/context/AuthContext';
import { ModalType, useModalContext } from '@/context/ModalContext';
import { useRecaptcha } from '@/hooks/useRecaptcha';
import { OlympCaptchaDestination } from '@/Olymp/types';
import { Recaptcha, RecaptchaVersion } from '../../Recaptcha';
import { useTranslation } from '@/hooks/useTranslation';
import { TermsText } from '@/components/TermsText';
import { Loader } from '@/components/Loader';
import { UseRequest } from '@/hooks/useRequest';
import { UseFormReturn } from 'react-hook-form';
import { ResetPasswordButton } from '@/components/ResetPasswordButton';
import { useBoolean } from '@/hooks/useBoolean';
import { useMobile } from '@/hooks/useMobile';
import { InputPasswordVisibility } from '@/components/InputPasswordVisibility';
import { AnalyticClient, DialogErrorShowEventFrom, trackDialogErrorShow, RequestStatuses } from '@repo/utils';
import { formatFieldErrors } from '@/utils/error/formatFieldErrors';
import { useAnalyticsAuthForm } from '@/hooks/useAnalyticsAuthForm';

interface Props {
  type: AuthTabsValues;
  Request: UseRequest;
  FormState: UseFormReturn<{ email: string; password: string }>;
  onRestorePassword?: () => void;
}

const AuthForm: React.FC<Props> = ({ type, Request, FormState, onRestorePassword }) => {
  const { t } = useTranslation();
  const { meState, getCheckCaptcha } = useAuthContext();
  const { open } = useModalContext();
  const showPassword = useBoolean();
  const isMobile = useMobile();
  const isRegister = type === 'register';
  const isLogin = type === 'login';
  const {
    handleSubmit: onSubmit,
    formState: { isSubmitted, errors },
    control,
    clearErrors,
  } = FormState;
  const recaptcha = useRecaptcha(isLogin ? OlympCaptchaDestination.Login : OlympCaptchaDestination.Registration);
  useAnalyticsAuthForm(Request.state, type);
  const showLoader =
    Request.state.isProcessing ||
    Request.state.status === RequestStatuses.Succeeded ||
    Boolean(meState?.value?.traderId);

  const handleSubmit = onSubmit((data) => {
    if (!recaptcha.isValid()) {
      return;
    }

    if (isRegister) {
      AnalyticClient.track({ type: 'Registration - Click', data: { email: data.email } });
    }

    if (isLogin) {
      AnalyticClient.track(
        { type: 'Login - Click', data: { email: data.email } },
        { amplitudeOptions: { persist: true }, nisyOptions: { persist: true } },
      );
    }

    Request.onRequest({
      data: {
        email: data.email,
        password: data.password,
        ad_id: AnalyticClient.getUid(),
        appVersion: AnalyticClient.getWebAppVersion(),
        recaptchaValue: recaptcha.value,
      },
    });
  });

  useEffect(() => {
    if (!isSubmitted) {
      return;
    }
    recaptcha.isValid();
  }, [isSubmitted]);

  useEffect(() => {
    const hasErrors = Boolean(Request.state.errors?.length);
    if (hasErrors) {
      const error = Request.state.errors?.[0]!;
      const errorMessage = error?.message;
      const modalType = errorMessage as unknown as ModalType;
      trackDialogErrorShow({
        code: modalType,
        from: type === AuthTabsValues.Login ? DialogErrorShowEventFrom.Login : DialogErrorShowEventFrom.Registration,
      });
      open(modalType);
      clearErrors?.();
      getCheckCaptcha();
    }
  }, [Request.state.errors, Request.state.status]);

  useEffect(
    () => () => {
      Request.onClearState();
    },
    [],
  );

  return (
    <Box component="form" sx={{ gap: '12px' }} onSubmit={handleSubmit}>
      <Controller
        name="email"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            {...formatFieldErrors({
              t,
              params: { field: t('field.email.placeholder') },
              errorType: errors?.email?.type,
            })}
            fullWidth
            sx={{ marginBottom: isMobile ? '16px' : '12px' }}
            type="email"
            placeholder={t('field.email.placeholder')}
          />
        )}
      />
      <Controller
        name="password"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            {...formatFieldErrors({
              t,
              params: {
                field: t('field.password.placeholder'),
                min: 4,
              },
              errorType: errors?.password?.type,
            })}
            fullWidth
            sx={{ marginBottom: '16px' }}
            type={showPassword.value ? 'text' : 'password'}
            placeholder={t('field.password.placeholder')}
            InputProps={{
              endAdornment: (
                <InputPasswordVisibility
                  position="end"
                  show={showPassword.value}
                  onToggle={showPassword.onToggle}
                  sx={{ opacity: 0.4 }}
                />
              ),
            }}
          />
        )}
      />
      {recaptcha.visible && recaptcha.key && (
        <Recaptcha
          version={recaptcha.version as RecaptchaVersion}
          captchaKey={recaptcha.key}
          timestamp={recaptcha.timestamp}
          error={recaptcha.error}
          onChange={recaptcha.onChange}
        />
      )}
      {isLogin && <ResetPasswordButton onClick={onRestorePassword} />}
      <Button
        fullWidth
        disabled={showLoader}
        type="submit"
        variant="contained"
        color="primary"
        sx={{ marginTop: '8px' }}
      >
        {showLoader ? <Loader /> : t(isRegister ? 'text.register' : 'text.login')}
      </Button>
      {isRegister && <TermsText />}
    </Box>
  );
};

export default AuthForm;
